import { useCallback, useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";

import useToastActionWrapper from "./useToastActionWrapper";
import {
  assignTechnicalNames,
  generateContractDraft,
  getContractDraftStatus,
  getProspect,
  getProspectAudit,
  setProspectState,
  updateProspect,
} from "../services/onboarding/prospects";
import {
  confirmSigned as confirmSignedApi,
  markEsignSent as markEsignSentApi,
  sendContract as sendContractApi,
  sendHubSigningLink as sendHubSigningLinkApi,
  uploadSignedContract as uploadSignedContractApi,
} from "../services/onboarding/contracts";
import {
  contractDraftErrorMessage,
  contractDraftFailureMessage,
} from "../pages/Onboarding/Prospects/components/contractDraftErrors";
import { contractDraftPolling } from "../utils/contractDraftPolling";
import { sendContractErrorMessage } from "../pages/Onboarding/Prospects/components/contractErrors";
import {
  AuditEntry,
  ContractDraftStatus,
  ProspectDetail,
  ProspectUpdatePayload,
  SetStatePayload,
  StateTransition,
} from "../types/onboarding";

interface UseProspectDetailParams {
  id: string;
  toastRef: React.RefObject<Toast>;
}

interface UseProspectDetail {
  prospect: ProspectDetail | null;
  transitions: StateTransition[];
  audit: AuditEntry[];
  loading: boolean;
  error: boolean;
  reload: () => Promise<void>;
  // Mutations — each refetches detail + audit on success so the UI reflects the
  // server snapshot (handles backend auto-clear of moreInfoNeeded + last-write-wins).
  setState: (payload: SetStatePayload, successMessage: string) => Promise<void>;
  saveEdit: (payload: ProspectUpdatePayload) => Promise<boolean>;
  assignNames: (customNames?: string[]) => Promise<boolean>;
  // Generate (or re-generate) the contract draft. The draft is made in the
  // background (CWA-187), so this resolves once it has finished: true after a
  // refetch on success, false (with a specific toast) on a rejection, a failed
  // generation or when waiting ran out.
  generateDraft: () => Promise<boolean>;
  // The latest draft request, and whether one is still being generated — also
  // true when the page was opened while an earlier request was running.
  draftStatus: ContractDraftStatus | null;
  draftGenerating: boolean;
  // Contract-signing send actions (US1). Each reloads on success and maps the
  // backend rejection to a specific toast on failure. One is offered at a time
  // depending on the prospect's signature method.
  sendContract: () => Promise<boolean>; // MANUAL → CONTRACT_SENT
  markEsignSent: () => Promise<boolean>; // DIGITAL → ESIGN_AWAITED
  sendHubLink: () => Promise<boolean>; // CLICK_TO_SIGN → CONTRACT_SENT
  // Contract-signing record actions (US2). Reload + success toast on success;
  // errors propagate so the calling dialog can show an inline message and stay
  // open (mirrors saveEdit).
  uploadSigned: (pdfBytes: ArrayBuffer | Blob) => Promise<boolean>; // MANUAL → CONTRACT_SIGNED
  confirmSigned: (signedContractUrl: string) => Promise<boolean>; // DIGITAL → CONTRACT_SIGNED
}

/**
 * Owns a single prospect's detail, transition history, and audit log, plus the
 * ops mutations (status change, edit, technical-name assignment). All mutations
 * reload from the server rather than mutating local state optimistically.
 */
const useProspectDetail = ({
  id,
  toastRef,
}: UseProspectDetailParams): UseProspectDetail => {
  const { toastActionWrapper } = useToastActionWrapper(toastRef);
  const [prospect, setProspect] = useState<ProspectDetail | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [draftStatus, setDraftStatus] = useState<ContractDraftStatus | null>(
    null
  );
  // Each visit of a prospect gets its own token: the route reuses this page for
  // every /prospects/:id, so the token changes on arriving and on leaving, and
  // async work captures it when it starts and drops its results once it no
  // longer matches — including after A → B → A, where the id alone matches again.
  const visit = useRef(0);
  const isCurrent = (token: number) => visit.current === token;
  // One wait per visit at a time, shared by whoever needs the outcome.
  const draftWait = useRef<{
    token: number;
    wait: Promise<ContractDraftStatus | null>;
  } | null>(null);

  // Declared before the effect that loads the prospect, so the token for this
  // visit exists before anything captures it.
  useEffect(() => {
    visit.current += 1;
    return () => {
      visit.current += 1;
      draftWait.current = null;
    };
  }, [id]);

  const load = useCallback(
    async (token: number) => {
      setLoading(true);
      setError(false);
      try {
        const [detail, auditRes] = await Promise.all([
          getProspect(id),
          getProspectAudit(id),
        ]);
        if (!isCurrent(token)) return;
        setProspect(detail);
        setAudit(auditRes?.entries ?? []);
      } catch (e) {
        if (!isCurrent(token)) return;
        console.error("Failed to load prospect detail", e);
        setError(true);
        toastRef.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load this prospect.",
          life: 3000,
        });
      } finally {
        if (isCurrent(token)) setLoading(false);
      }
    },
    // isCurrent reads a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, toastRef]
  );

  const reload = useCallback(() => load(visit.current), [load]);

  useEffect(() => {
    reload();
  }, [reload]);

  const setState = useCallback(
    async (payload: SetStatePayload, successMessage: string) => {
      await toastActionWrapper({
        action: async () => {
          await setProspectState(id, payload);
          await reload();
          return true;
        },
        successMessage,
        errorMessage: "The status could not be updated.",
      });
    },
    [id, reload, toastActionWrapper]
  );

  // saveEdit/assignNames return a boolean so dialogs can stay open on failure
  // (e.g. to show server field errors) and close on success.
  const saveEdit = useCallback(
    async (payload: ProspectUpdatePayload): Promise<boolean> => {
      try {
        await updateProspect(id, payload);
        await reload();
        toastRef.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Prospect updated.",
          life: 3000,
        });
        return true;
      } catch (e) {
        throw e; // dialog maps 422 field errors; re-thrown for handling there
      }
    },
    [id, reload, toastRef]
  );

  const assignNames = useCallback(
    async (customNames?: string[]): Promise<boolean> => {
      try {
        await assignTechnicalNames(id, customNames);
        await reload();
        toastRef.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Technical names assigned.",
          life: 3000,
        });
        return true;
      } catch (e) {
        throw e;
      }
    },
    [id, reload, toastRef]
  );

  // Poll the draft status until the background generation has finished.
  // Resolves with the final status, or null when the wait ran out (or the
  // visit ended). A failed check is not the end of the wait.
  const waitForDraft = useCallback(
    (token: number): Promise<ContractDraftStatus | null> => {
      if (draftWait.current?.token === token) return draftWait.current.wait;
      const wait = (async () => {
        const started = Date.now();
        while (isCurrent(token)) {
          const elapsed = Date.now() - started;
          if (elapsed >= contractDraftPolling.maxWaitMs) return null;
          await sleep(
            elapsed < contractDraftPolling.fastPhaseMs
              ? contractDraftPolling.fastIntervalMs
              : contractDraftPolling.slowIntervalMs
          );
          if (!isCurrent(token)) return null;
          try {
            const status = await getContractDraftStatus(id);
            if (!isCurrent(token)) return null;
            if (status) setDraftStatus(status);
            if (status && status.status !== "PENDING") return status;
          } catch (e) {
            console.warn("Contract draft status check failed; retrying", e);
          }
        }
        return null;
      })().finally(() => {
        if (draftWait.current?.wait === wait) draftWait.current = null;
      });
      draftWait.current = { token, wait };
      return wait;
    },
    // isCurrent reads a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id]
  );

  // Tell ops how the generation ended — only while the visit that waited for
  // it is still on screen.
  const finishDraft = useCallback(
    async (
      status: ContractDraftStatus | null,
      token: number
    ): Promise<boolean> => {
      if (!isCurrent(token)) return false;
      if (status?.status === "SUCCEEDED") {
        await load(token);
        if (!isCurrent(token)) return false;
        toastRef.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Contract draft generated.",
          life: 3000,
        });
        return true;
      }
      if (status === null) {
        // Stop showing it as generating, so the draft can be requested again —
        // the server lets a stuck request be replaced after the same 15 minutes.
        setDraftStatus(null);
        toastRef.current?.show({
          severity: "warn",
          summary: "Contract draft still generating",
          detail:
            "Generating the draft is taking unusually long. Refresh the page later to see whether it's ready.",
          life: 8000,
        });
        return false;
      }
      toastRef.current?.show({
        severity: "error",
        summary: "Contract draft not generated",
        detail: contractDraftFailureMessage(status),
        life: 8000,
      });
      return false;
    },
    // isCurrent reads a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [load, toastRef]
  );

  // Opening a prospect whose draft is still generating picks the wait back up.
  useEffect(() => {
    const token = visit.current;
    setDraftStatus(null);
    getContractDraftStatus(id)
      .then(async (status) => {
        if (!isCurrent(token) || !status) return;
        setDraftStatus(status);
        if (status.status === "PENDING") {
          await finishDraft(await waitForDraft(token), token);
        }
      })
      .catch((e) => console.warn("Failed to load the contract draft status", e));
    // finishDraft/waitForDraft change with `id` only; isCurrent reads a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const generateDraft = useCallback(async (): Promise<boolean> => {
    const token = visit.current;
    let status: ContractDraftStatus;
    try {
      status = await generateContractDraft(id);
    } catch (e) {
      if (!isCurrent(token)) return false;
      toastRef.current?.show({
        severity: "error",
        summary: "Contract draft not generated",
        detail: contractDraftErrorMessage(e),
        life: 6000,
      });
      return false;
    }
    if (!isCurrent(token)) return false;
    setDraftStatus(status);
    return finishDraft(
      status?.status === "PENDING" ? await waitForDraft(token) : status,
      token
    );
    // isCurrent reads a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, finishDraft, waitForDraft, toastRef]);

  // Shared runner for contract-signing actions: reload the server snapshot on
  // success, map the structured rejection to a specific toast on failure.
  const runContractAction = useCallback(
    async (
      fn: () => Promise<unknown>,
      successMessage: string,
      errorSummary: string
    ): Promise<boolean> => {
      try {
        await fn();
        await reload();
        toastRef.current?.show({
          severity: "success",
          summary: "Success",
          detail: successMessage,
          life: 3000,
        });
        return true;
      } catch (e) {
        toastRef.current?.show({
          severity: "error",
          summary: errorSummary,
          detail: sendContractErrorMessage(e),
          life: 6000,
        });
        return false;
      }
    },
    [reload, toastRef]
  );

  const sendContract = useCallback(
    () =>
      runContractAction(
        () => sendContractApi(id),
        "Contract sent to the prospect.",
        "Contract not sent"
      ),
    [id, runContractAction]
  );

  const markEsignSent = useCallback(
    () =>
      runContractAction(
        () => markEsignSentApi(id),
        "Marked as e-signature sent.",
        "Could not mark e-signature as sent"
      ),
    [id, runContractAction]
  );

  const sendHubLink = useCallback(
    () =>
      runContractAction(
        () => sendHubSigningLinkApi(id),
        "Signing link sent to the prospect.",
        "Signing link not sent"
      ),
    [id, runContractAction]
  );

  const uploadSigned = useCallback(
    async (pdfBytes: ArrayBuffer | Blob): Promise<boolean> => {
      await uploadSignedContractApi(id, pdfBytes);
      await reload();
      toastRef.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Signed contract recorded.",
        life: 3000,
      });
      return true;
    },
    [id, reload, toastRef]
  );

  const confirmSigned = useCallback(
    async (signedContractUrl: string): Promise<boolean> => {
      await confirmSignedApi(id, signedContractUrl);
      await reload();
      toastRef.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Signature confirmed.",
        life: 3000,
      });
      return true;
    },
    [id, reload, toastRef]
  );

  return {
    prospect,
    transitions: prospect?.transitions ?? [],
    audit,
    loading,
    error,
    reload,
    setState,
    saveEdit,
    assignNames,
    generateDraft,
    draftStatus,
    draftGenerating: draftStatus?.status === "PENDING",
    sendContract,
    markEsignSent,
    sendHubLink,
    uploadSigned,
    confirmSigned,
  };
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export default useProspectDetail;
